import { cardCoverFrame, type CardCoverSpec } from './cardCovers';
import {
  cardText,
  cardTypeCss,
  cardWatermark,
  fitCardText,
  round2,
  type KitCardFont,
  type KitCardItem,
  type KitCardLayout,
  type KitCardLayoutInput,
  type KitThumbnailCard,
} from './kitCard';
import { storyThemeIdOf } from './storyFrames';
import type { StoryBox } from './storyLayout';
import { groundDots, mixHex, storyAlpha, TORN_TOP_PATH, type StoryColours, type StoryCss } from './storyStyles';
import { WATERMARK_PLACEMENTS } from './watermark';
import type { ReleaseThemeId } from '../releaseThemes';

/**
 * The YouTube thumbnail (design `asset-redesign/i YouTube thumbnail - new`, `ThumbnailFrame.dc.html`,
 * owner answers 2026-10-08): one thumbnail per kit video. The cover in the frame of the theme on
 * the left, a text column on the right with the video label ("Lyric video"), the title one time and
 * the artist. A long title that needs two lines drops the artist. No status, no URL. The length
 * badge zone and the watched bar hold only the ground. Every size is in px of the 1280 x 720 file. Pure.
 */

export const THUMBNAIL_SIZE = { width: 1280, height: 720 } as const;
/** The content box (x 64 to 1160, y 64 to 656). */
export const THUMBNAIL_BOX: StoryBox = { x: 64, y: 64, w: 1096, h: 592 };
/** The length badge zone at the smallest thumbnail (168 px): ground only. */
export const THUMBNAIL_BADGE_ZONE: StoryBox = { x: 960, y: 560, w: 320, h: 160 };
/** The text column ends above the badge zone. */
export const THUMBNAIL_COLUMN_BOTTOM = 560;

/** The thumbnail factors of each theme (`k / t` of page i): the title reaches the legibility floor in one line. */
export const THUMBNAIL_FACTORS: Readonly<Record<ReleaseThemeId, { display: number; text: number }>> = {
  clean: { display: 1, text: 1 },
  hype: { display: 1.22, text: 1 },
  dreamy: { display: 1.3, text: 0.95 },
  dark: { display: 0.86, text: 0.9 },
  warm: { display: 1, text: 1 },
  retro: { display: 0.8, text: 0.95 },
  neon: { display: 0.7, text: 0.9 },
  handmade: { display: 1.4, text: 1.2 },
};

/** The sizes of page i ("Sizes · 1280 x 720"). */
export const THUMBNAIL_SIZES = {
  cover: 520,
  /** Hype, Noir and Neon: 500. Retro and Handmade: 460. */
  coverMid: 500,
  coverSmall: 460,
  rowGap: 48,
  /** The room for the Retro record right of the sleeve. */
  retroRecordRoom: 96,
  padLeft: 8,
  /** Hype and Handmade: the turned cover needs room on the left. */
  padLeftTurned: 20,
  columnGap: 18,
  columnMaxWidth: 520,
  labelHeight: 76,
  labelPadX: 32,
  label: 44,
  title: 128,
  artist: 56,
  noirLine: { w: 140, h: 8 },
} as const;

/** A long title shrinks to 75 % of its size, then wraps to two lines. */
const TITLE_FLOOR = 0.75;
const LINE = { display: 0.92, text: 1.1 } as const;

const COVER_SPEC: CardCoverSpec = {
  clean: { radius: 22, shadow: [34, 60, -26] },
  hype: { turn: -4, border: 14, shadow: 18 },
  dreamy: { radius: 56, glow: [30, 80, -20] },
  dark: { pad: 16, outline: 3, grey: 0.35 },
  warm: { radius: 24, shadow: [30, 60, -26] },
  retro: { border: 12, shadow: [22, 40, -22], record: { top: '8%', right: '-22%', width: '84%', inset: 20 } },
  neon: { pad: 12, border: 6, radius: 30, glow: 28, inset: 18, pictureRadius: 16 },
  handmade: { pad: [16, 16, 56], turn: -4, shadow: [22, 36, -18], tape: [-24, 48] },
};

function coverWidth(id: ReleaseThemeId): number {
  if (id === 'retro' || id === 'handmade') {
    return THUMBNAIL_SIZES.coverSmall;
  }
  return id === 'hype' || id === 'dark' || id === 'neon' ? THUMBNAIL_SIZES.coverMid : THUMBNAIL_SIZES.cover;
}

/** The colour of the title: the accent, or the text colour (Noir, Retro, Neon). */
function heroColour(id: ReleaseThemeId, colors: StoryColours): string {
  return id === 'dark' || id === 'retro' || id === 'neon' ? colors.text : colors.accent;
}

/** The label pill of each theme (page i, "Theme frame table": the label column). */
function labelCss(id: ReleaseThemeId, colors: StoryColours): StoryCss {
  const pill: StoryCss = {
    padding: `0 ${THUMBNAIL_SIZES.labelPadX}px`,
    borderRadius: 999,
    fontWeight: 800,
  };
  switch (id) {
    case 'hype':
      return { ...pill, background: colors.surface, color: colors.accent, transform: 'rotate(-3deg)' };
    case 'dark':
    case 'handmade':
      return { ...pill, background: colors.text, color: colors.ink };
    case 'neon':
      return {
        ...pill,
        background: 'transparent',
        color: colors.accent,
        border: `5px solid ${colors.accent}`,
        boxShadow: `0 0 22px ${colors.accent}, inset 0 0 12px ${storyAlpha(colors.accent, 0.5)}`,
        textShadow: `0 0 10px ${colors.accent}`,
      };
    case 'clean':
    case 'dreamy':
    case 'warm':
    case 'retro':
      return { ...pill, background: colors.accent, color: colors.onAccent };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

function titleCss(id: ReleaseThemeId, colors: StoryColours): StoryCss {
  const color = heroColour(id, colors);
  switch (id) {
    case 'hype':
      return { color, textShadow: `8px 8px 0 ${colors.surface}` };
    case 'retro':
      return { color, textShadow: `6px 6px 0 ${colors.accent}` };
    case 'neon':
      return { color, textShadow: '0 0 12px currentColor, 0 0 34px currentColor' };
    case 'handmade':
      return { color, transform: 'rotate(-3deg)' };
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
      return { color };
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/** The ground of the thumbnail of each theme (`ThumbnailFrame` `.tf-ground`). */
export function thumbnailGround(themeId: string, colors: StoryColours): string | null {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'clean':
      return `radial-gradient(ellipse 55% 75% at 30% 50%, ${storyAlpha(colors.surface, 0.9)}, transparent 75%)`;
    case 'hype':
      return `radial-gradient(ellipse 50% 60% at 30% 50%, ${storyAlpha(colors.accent, 0.24)}, transparent 70%)`;
    case 'dreamy':
      return [
        'radial-gradient(ellipse 50% 45% at 70% 0%, rgba(255,255,255,.55), transparent 70%)',
        `radial-gradient(ellipse 60% 50% at 30% 100%, ${storyAlpha(colors.accent, 0.25)}, transparent 70%)`,
        `linear-gradient(180deg, ${colors.ink} 0%, ${colors.surface} 100%)`,
      ].join(', ');
    case 'dark':
      return `${groundDots('rgba(255,255,255,.07)', 6, 1)}, radial-gradient(ellipse 45% 70% at 28% 50%, ${colors.surface}, transparent 75%)`;
    case 'warm':
      return `radial-gradient(ellipse 45% 70% at 25% 25%, ${storyAlpha(colors.accent, 0.3)}, transparent 70%), linear-gradient(180deg, ${colors.surface} 0%, ${colors.ink} 70%)`;
    case 'retro':
      return groundDots('rgba(255,255,255,.08)', 6, 1);
    case 'neon':
      return `radial-gradient(ellipse 40% 60% at 25% 40%, ${storyAlpha(colors.accent, 0.22)}, transparent 70%), radial-gradient(ellipse 40% 50% at 70% 30%, ${storyAlpha(colors.text, 0.22)}, transparent 70%)`;
    case 'handmade':
      return `${groundDots('rgba(80,50,30,.05)', 9, 1)}, radial-gradient(ellipse 60% 70% at 35% 45%, rgba(255,255,255,.5), transparent 75%)`;
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

const abs = (style: StoryCss): StoryCss => ({ position: 'absolute', ...style });

/**
 * The ornaments of the thumbnail (`.tf-deco`): only on the left, so the badge zone (x 960 to 1280,
 * y 560 to 720) holds only the ground. Hype: stripes from the top-left and the bottom-left. Retro:
 * three arcs from the bottom-left. Handmade: the torn strip under the left 60 %.
 */
export function thumbnailDeco(themeId: string, colors: StoryColours): StoryCss[] {
  const id = storyThemeIdOf(themeId);
  switch (id) {
    case 'hype':
      return [
        abs({
          left: '-6%',
          width: '50%',
          top: '-3%',
          height: '9%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          left: '-6%',
          width: '44%',
          bottom: '1%',
          height: '6%',
          background: colors.surface,
          transform: 'rotate(-6deg)',
        }),
        abs({
          left: '-6%',
          width: '40%',
          bottom: '9%',
          height: '1.4%',
          background: colors.accent,
          transform: 'rotate(-6deg)',
        }),
      ];
    case 'retro': {
      const arc = (top: string, background: string): StoryCss =>
        abs({ left: '-30%', width: '85%', top, height: '120%', borderRadius: '50%', background });
      return [arc('80%', mixHex(colors.text, colors.ink, 0.7)), arc('83%', colors.surface), arc('86%', colors.accent)];
    }
    case 'handmade':
      return [
        abs({
          left: '-2%',
          width: '62%',
          bottom: '-1%',
          height: '7%',
          background: colors.surface,
          transform: 'rotate(-1.5deg)',
          clipPath: TORN_TOP_PATH,
        }),
      ];
    case 'clean':
    case 'dreamy':
    case 'dark':
    case 'warm':
    case 'neon':
      return [];
    default: {
      const exhaustive: never = id;
      return exhaustive;
    }
  }
}

/**
 * The layout of one thumbnail: the cover at the left of the content box and the text column right
 * of it, both centred on the height. Order in the column: label, title, artist (Noir: an accent
 * line under the title). Pure.
 */
export function thumbnailLayout(input: KitCardLayoutInput<KitThumbnailCard>): KitCardLayout {
  const { card, themeId, colors } = input;
  const id = storyThemeIdOf(themeId);
  const factors = THUMBNAIL_FACTORS[id];
  const display: KitCardFont = { id: input.fonts.display, style: input.typeStyle.display };
  const text: KitCardFont = { id: input.fonts.text, style: input.typeStyle.text };
  const labelFont: KitCardFont = {
    id: text.id,
    style: { ...text.style, weight: 800, tracking: Math.min(text.style.tracking, 0.08) },
  };
  const frame = cardCoverFrame(themeId, colors, COVER_SPEC);
  const coverW = coverWidth(id);
  const coverH = frame.heightOf(coverW);
  const padLeft = id === 'hype' || id === 'handmade' ? THUMBNAIL_SIZES.padLeftTurned : THUMBNAIL_SIZES.padLeft;
  const gap = THUMBNAIL_SIZES.rowGap + (id === 'retro' ? THUMBNAIL_SIZES.retroRecordRoom : 0);
  const coverX = THUMBNAIL_BOX.x + padLeft;
  const columnX = coverX + coverW + gap;
  const columnW = Math.min(THUMBNAIL_SIZES.columnMaxWidth, THUMBNAIL_BOX.x + THUMBNAIL_BOX.w - columnX);

  const labelFit = fitCardText(
    card.label,
    labelFont,
    THUMBNAIL_SIZES.label * factors.text,
    columnW - 2 * THUMBNAIL_SIZES.labelPadX
  );
  const markerPad = id === 'handmade' ? 28 : 0;
  const titleFit = fitCardText(
    card.title,
    display,
    THUMBNAIL_SIZES.title * factors.display * input.typeStyle.display.scale,
    columnW - markerPad,
    { maxLines: 2, floor: TITLE_FLOOR }
  );
  // Owner answer 3 of page i: a title on two lines drops the artist (the channel name shows it).
  const withArtist = titleFit.lines.length === 1;
  const artistFont: KitCardFont = { id: text.id, style: { ...text.style, weight: 700 } };
  const artistFit = fitCardText(card.artist, artistFont, THUMBNAIL_SIZES.artist * factors.text, columnW);

  const labelLineHeight = (THUMBNAIL_SIZES.labelHeight - 2 * (id === 'neon' ? 5 : 0)) / labelFit.sizePx;
  const parts: Array<(top: number) => KitCardItem> = [
    (top) =>
      cardText({
        role: 'label',
        x: columnX,
        y: top,
        lines: labelFit.lines,
        sizePx: labelFit.sizePx,
        lineHeight: labelLineHeight,
        font: labelFont,
        align: 'center',
        chip: { padX: THUMBNAIL_SIZES.labelPadX, padY: 0, border: id === 'neon' ? 5 : 0 },
        css: { ...cardTypeCss(labelFont.style), ...labelCss(id, colors) },
      }),
    (top) => {
      const item = cardText({
        role: 'title',
        x: columnX,
        y: top,
        lines: titleFit.lines,
        sizePx: titleFit.sizePx,
        lineHeight: LINE.display,
        font: display,
        align: 'left',
        css: { ...cardTypeCss(display.style), ...titleCss(id, colors) },
        lineCss:
          id === 'handmade'
            ? {
                background: `linear-gradient(transparent 60%, ${storyAlpha(colors.accent, 0.45)} 60% 92%, transparent 92%)`,
                padding: '0 14px',
              }
            : undefined,
      });
      return markerPad === 0 ? item : { ...item, box: { ...item.box, w: round2(item.box.w + markerPad) } };
    },
  ];
  if (id === 'dark') {
    parts.push((top) => ({
      kind: 'shape',
      role: 'line',
      box: { x: columnX, y: round2(top), w: THUMBNAIL_SIZES.noirLine.w, h: THUMBNAIL_SIZES.noirLine.h },
      css: { background: colors.accent },
    }));
  }
  if (withArtist) {
    parts.push((top) =>
      cardText({
        role: 'artist',
        x: columnX,
        y: top,
        lines: artistFit.lines,
        sizePx: artistFit.sizePx,
        lineHeight: LINE.text,
        font: artistFont,
        align: 'left',
        css: { ...cardTypeCss(artistFont.style), color: colors.text },
      })
    );
  }
  if (input.watermark === true) {
    // Design w0: a stack lockup, L 40, the last item of the column; the logo alone when the column is narrow.
    parts.push((top) =>
      cardWatermark({
        x: columnX,
        y: top,
        form: WATERMARK_PLACEMENTS.thumbnail.form,
        L: WATERMARK_PLACEMENTS.thumbnail.L,
        maxWidth: columnW,
        align: 'left',
      })
    );
  }
  const column: KitCardItem[] = [];
  let bottom = 0;
  parts.forEach((part, index) => {
    const item = part(bottom);
    column.push(item);
    bottom = item.box.y + item.box.h + (index < parts.length - 1 ? THUMBNAIL_SIZES.columnGap : 0);
  });
  const columnH = bottom;
  // The column is centred on the cover, and it ends above the badge zone.
  const centre = THUMBNAIL_BOX.y + THUMBNAIL_BOX.h / 2;
  const columnTop = Math.min(centre - columnH / 2, THUMBNAIL_COLUMN_BOTTOM - columnH);
  const coverTop = centre - coverH / 2;
  const items: KitCardItem[] = [
    {
      kind: 'cover',
      box: { x: coverX, y: round2(coverTop), w: coverW, h: round2(coverH) },
      outer: frame.outer,
      picture: frame.picture,
      tape: frame.tape,
      record: frame.record,
    },
    ...column.map((item) => ({ ...item, box: { ...item.box, y: round2(item.box.y + columnTop) } })),
  ];
  return {
    size: THUMBNAIL_SIZE,
    ground: thumbnailGround(themeId, colors),
    deco: thumbnailDeco(themeId, colors),
    items,
  };
}
